import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Paper, Button } from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Close,
  Save,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  createServiceCommunicationThunk,
  updateServiceCommunicationThunk,
} from "../../../../store/slices/CommunicationServiceSlice";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import EmailEditor from "./EmailEditor";
import SmsEditor from "./SmsEditor";

const { toast } = require("react-toastify");

// parse array from string/JSON/csv for required_variables
function parseArraySafe(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : value ? [value] : [];
    } catch {
      return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function CreateFormBuilder({
  isOpen,
  toggle,
  data,
  fetchData,
  dispatch,
  alignment, // 'email' | 'sms'
}) {
  const isEmail = alignment === "email" || data?.communication_type === "email";

  // ---------- Stable defaults ----------
  const defaultValues = useMemo(
    () => ({
      template_key: "",
      template_name: "",
      communication_type: isEmail ? "email" : "sms",
      // email
      email_from: "",
      email_bcc: "",
      email_subject: "",
      email_html_content: "",
      // sms
      sms_template_id: "",
      sms_header: "",
      sms_text_template: "",
      // common
      required_variables: "",
      tenant_id: "",
    }),
    [isEmail],
  );

  // ---------- Local controlled state (no react-hook-form) ----------
  const [form, setForm] = useState(defaultValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewport, setViewport] = React.useState("desktop");

  // Initialize or load record when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    if (data?.id) {
      // Ensure communication_type matches the current alignment
      setForm({ ...data, communication_type: isEmail ? "email" : "sms" });
    } else {
      setForm(defaultValues);
    }
    setErrors({});
  }, [isOpen, data?.id, data, defaultValues, isEmail]);

  const updateField = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" })); // clear field error on change
  };

  // ---------- Validation ----------
  const validate = useCallback(() => {
    const e = {};
    if (!form.template_key?.trim()) e.template_key = "Template key is required";
    // if (!form.template_name?.trim())
    //   e.template_name = "Template name is required";
    if (!form.tenant_id?.trim()) e.tenant_id = "Tenant id is required";
    if (isEmail) {
      // if (!form.email_subject?.trim()) e.email_subject = "Subject is required";
      // Optional: you can enforce email_from required as well
      // if (!form.email_from?.trim()) e.email_from = "From is required";
      // if (!form.email_html_content?.trim())
      //   e.email_html_content = "Email HTML content is required";
    } else {
      // if (!form.sms_text_template?.trim())
      //   e.sms_text_template = "SMS text template is required";
      // Optional: you can enforce sms_header or sms_template_id as required
      // if (!form.sms_header?.trim()) e.sms_header = "SMS header is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form, isEmail]);

  // ---------- Submit ----------
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      if (form.id) {
        const {
          id,
          created_at,
          updated_at,
          email_bcc: bcc,
          required_variables: required_variables_str,
          ...rest
        } = form;
        let bccArray = parseArraySafe(bcc);
        let requiredVariablesArray = parseArraySafe(required_variables_str);
        const response = await dispatch(
          updateServiceCommunicationThunk({
            id,
            ...rest,
            email_bcc: JSON.stringify(bccArray),
            required_variables: JSON.stringify(requiredVariablesArray),
          }),
        );
        setIsSubmitting(true);
        if (response.success || !response.error) {
          Swal.fire("Updated!", "The form has been updated.", "success");
          toggle();
          fetchData?.();
        } else {
          Swal.fire(
            "Error!",
            "Template key with same tenant and communication type may already exist.",
            "error",
          );
        }
        setIsSubmitting(false);
      } else {
        const {
          email_bcc: bcc,
          required_variables: required_variables_str,
          ...rest
        } = form;
        const bccArray = parseArraySafe(bcc);
        const requiredVariablesArray = parseArraySafe(required_variables_str);
        const response = await dispatch(
          createServiceCommunicationThunk({
            ...rest,
            email_bcc: JSON.stringify(bccArray),
            required_variables: JSON.stringify(requiredVariablesArray),
          }),
        );
        setIsSubmitting(true);
        if (response.success || !response.error) {
          Swal.fire("Created!", "The form has been created.", "success");
          toggle();
          fetchData?.();
        } else {
          Swal.fire(
            "Error!",
            "Template key with same tenant and communication type may already exist.",
            "error",
          );
        }
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Failed to create/update the form.", "error");
      toast.error("Failed to create the form.");
    }
  };

  const tags = useMemo(() => {
    const arr = parseArraySafe(form.required_variables);
    if (arr.length) return arr;
    return isEmail ? [] : [];
  }, [isEmail, form.required_variables]);

  const closeBtn = (
    <Button
      variant="contained"
      outline
      className="close"
      style={{ marginLeft: "auto" }}
      onClick={toggle}
      type="button"
      aria-label="Close"
      title="Close"
    >
      &times;
    </Button>
  );

  // ---------- UI ----------
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      backdrop="static"
      style={{ maxWidth: 1200, width: "100%" }}
      fade={true}
      centered={true}
    >
      <ModalHeader close={closeBtn} className="w-full">
        {data?.id
          ? `Update ${isEmail ? "Email" : "SMS"} Template`
          : `Create ${isEmail ? "Email" : "SMS"} Template`}
      </ModalHeader>
      <form onSubmit={(e) => handleSave(e)}>
        <ModalBody style={{ background: "#f5f7fb" }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, sm: 2 },
              borderRadius: 2,
              bgcolor: "transparent",
            }}
          >
            {isEmail ? (
              <EmailEditor
                form={form}
                setViewport={setViewport}
                updateField={updateField}
                tags={tags}
                viewport={viewport}
                errors={errors}
              />
            ) : (
              <SmsEditor
                form={form}
                updateField={updateField}
                tags={tags}
                errors={errors}
              />
            )}
          </Paper>
        </ModalBody>
        <ModalFooter className="d-flex customModalFooter">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<Save />}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : data?.id ? "Update" : "Create"}
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            startIcon={<Close />}
            onClick={() => {
              if (!data || !data.id) {
                // only reset on create flow
                setForm(defaultValues);
              }
              toggle();
            }}
            disabled={isSubmitting}
            sx={{ ml: 1 }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default CreateFormBuilder;
